import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useI18n } from '../../context/I18nContext';
import { useAuth } from '../../context/AuthContext';
import { useLockBodyScroll } from '../../hooks/useLockBodyScroll';
import { authColors, shadows, neutralColors } from '../../styles/colors';
import { Home, DollarSign, Mail, LogIn, X, BookOpen } from 'lucide-react';
import Logo from '../common/Logo';
import { trackUmamiEvent } from '../../utils/umami';

interface PublicMobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

const PublicMobileMenu: React.FC<PublicMobileMenuProps> = ({ isOpen, onClose }) => {
  const { t } = useI18n();
  const { user } = useAuth();
  const location = useLocation();

  const navigation = [
    {
      name: t('landing.home'),
      href: '/',
      icon: Home,
    },
    {
      name: t('landing.pricing'),
      href: '/pricing',
      icon: DollarSign,
    },
    {
      name: t('blog.nav'),
      href: '/blog',
      icon: BookOpen,
    },
    {
      name: t('landing.contact'),
      href: '/contact',
      icon: Mail,
    },
  ];

  const handleLinkClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    onClose();
  };

  const handleNavClick = (href: string) => {
    if (href === '/pricing') {
      trackUmamiEvent('pricing_click', { source: 'mobile_menu' });
    }
    handleLinkClick();
  };

  useLockBodyScroll(isOpen);

  return (
    <>
      {/* Fullscreen Overlay Menu */}
      <div
        className={`
          fixed inset-0 z-[60] bg-white/95 backdrop-blur-md lg:hidden overflow-hidden
          transition-opacity duration-300 ease-out
          ${isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}
        `}
        aria-hidden={!isOpen}
      >
        {/* Header - Mêmes dimensions que PublicHeader (h-16 = py-4 environ) */}
        {/* Pas d'animation sur le header car il doit rester aligné avec le header navbar */}
        <div 
          className={`
            absolute top-0 left-0 right-0 h-16 px-4 flex items-center border-b ${neutralColors.grayBorder}
          `}
        >
          {/* Close Button - Left (même position que le burger menu) */}
          <div className="flex-shrink-0">
            <button
              onClick={onClose}
              className={`p-2 rounded-md ${neutralColors.grayTextLight} hover:text-gray-900 hover:${neutralColors.gray100} transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500`}
              aria-label="Close menu"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Logo - Center */}
          <div className="flex-1 flex justify-center">
            <Logo />
          </div>
          
          {/* Spacer - Right (pour équilibrer) */}
          <div className="w-10 flex-shrink-0"></div>
        </div>

        {/* Menu Content - Scrollable */}
        <div className="flex flex-col h-full pt-20 pb-8 px-4 overflow-y-auto overscroll-contain">
          {/* Navigation Items */}
          <nav className="flex-1 space-y-1 mt-8">
            {navigation.map((item, index) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => handleNavClick(item.href)}
                  className={`
                    group relative flex items-center justify-start px-3 py-3 rounded-xl transition-all duration-300
                    transform
                    ${isOpen ? 'translate-x-0 opacity-100' : 'translate-x-4 opacity-0'}
                    ${
                      isActive
                        ? `${authColors.primaryGradient} text-white ${shadows.medium}`
                        : `${neutralColors.grayText} hover:bg-blue-50 hover:text-gray-900`
                    }
                  `}
                  style={{ 
                    transitionDelay: isOpen ? `${200 + index * 75}ms` : '0ms' 
                  }}
                >
                  {/* Active indicator */}
                  {isActive && (
                    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-white rounded-r-full" />
                  )}
                  
                  {/* Icône dans un conteneur fixe */}
                  <span className="w-8 flex items-center justify-center flex-shrink-0">
                    <item.icon className={`w-5 h-5 transition-transform duration-200 ${isActive ? 'scale-110' : 'group-hover:scale-110'}`} />
                  </span>
                  
                  <span className="flex-1 text-base font-medium ml-2">{item.name}</span>
                </Link>
              );
            })}
          </nav>

          {/* Auth Actions - Bottom */}
          <div 
            className={`
              mt-8 pt-6 border-t ${neutralColors.grayBorder} space-y-3
              transform transition-all duration-700 ease-out
              ${isOpen ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}
            `}
            style={{ transitionDelay: isOpen ? '425ms' : '0ms' }}
          >
            {!user && (
              <>
                <Link
                  to="/signup"
                  onClick={() => {
                    trackUmamiEvent('signup_click', { source: 'mobile_menu' });
                    handleLinkClick();
                  }}
                  className={`
                    w-full flex items-center justify-center px-3 py-3.5 rounded-xl
                    ${authColors.primaryGradient} text-white ${shadows.medium}
                    transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]
                  `}
                >
                  <span className="text-base font-semibold">{t('landing.headerSignup')}</span>
                </Link>
                <Link
                  to="/login"
                  onClick={() => {
                    trackUmamiEvent('sign_in_click', { source: 'mobile_menu' });
                    handleLinkClick();
                  }}
                  className="w-full flex items-center justify-center py-3 text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
                >
                  {t('landing.signIn')}
                </Link>
              </>
            )}
            {user && (
              <Link
                to="/app"
                onClick={handleLinkClick}
                className={`
                  w-full relative overflow-hidden flex items-center justify-start px-3 py-3 rounded-xl
                  ${authColors.primaryGradient} text-white ${shadows.medium}
                  transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] group
                `}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />
                <span className="w-8 flex items-center justify-center flex-shrink-0 relative z-10">
                  <LogIn className="w-5 h-5" />
                </span>
                <span className="text-base font-semibold ml-2 relative z-10">
                  {t('nav.dashboard')}
                </span>
              </Link>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default PublicMobileMenu;

